/**
 * Count-up: every number inside an element marked data-count-up grows from 0 to the number written
 * in the HTML once the element enters the viewport.
 *
 *   <p data-count-up>40% less time</p>            → 0% … 40%
 *   <p data-count-up>230 → 930</p>               → each number counts on its own
 *   <p data-count-up data-count-duration="2400" data-count-delay="200">R$ 18,400</p>
 *
 * Only text nodes change, so markup, translated copy and the final text stay as written (the page reads
 * correctly without JS and with prefers-reduced-motion). Separators are kept as written ("18,400",
 * "1.5", "1.234,5"), as are decimals and leading zeros ("01").
 */

const NUMBER = /\d[\d.,]*\d|\d/g;
const DEFAULT_DURATION = 1600;

const easeOutCubic = t => 1 - (1 - t) ** 3;

/** Reads a written number ("1,234.5", "1.234,5", "40", "01") into its value and how to print it again. */
function parseNumber(token) {
  const separators = token.match(/[.,]/g) ?? [];
  const last = Math.max(token.lastIndexOf('.'), token.lastIndexOf(','));
  const tail = last === -1 ? '' : token.slice(last + 1);
  const mixed = separators.length > 1 && new Set(separators).size > 1;
  // "1,234" and "1.234.567" are grouped thousands; "1.5" and "1,25" are decimals.
  const hasDecimals = last !== -1 && (mixed || (separators.length === 1 && tail.length !== 3));
  const decimal = hasDecimals ? token[last] : '';
  const group = separators.find(separator => separator !== decimal) ?? '';
  const decimals = hasDecimals ? tail.length : 0;
  const digits = token.replaceAll(group || '\0', '').replace(decimal || '\0', '.');

  return {
    value: Number(digits),
    decimals,
    decimal,
    group,
    pad: /^0\d/.test(token) && !separators.length ? token.length : 0,
  };
}

function format(value, { decimals, decimal, group, pad }) {
  const [integer, fraction] = value.toFixed(decimals).split('.');
  const grouped = group ? integer.replace(/\B(?=(\d{3})+(?!\d))/g, group) : integer;

  return (pad ? grouped.padStart(pad, '0') : grouped) + (fraction ? decimal + fraction : '');
}

/** Splits the element's text nodes into fixed text and numbers; render(progress) writes them back. */
function prepare(element) {
  const parts = [];
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const numbers = [...node.data.matchAll(NUMBER)].map(match => ({
      index: match.index,
      length: match[0].length,
      ...parseNumber(match[0]),
    }));

    if (numbers.length > 0) {
      parts.push({ node, text: node.data, numbers });
    }
  }

  return progress => {
    parts.forEach(({ node, text, numbers }) => {
      let output = '';
      let cursor = 0;

      numbers.forEach(number => {
        output += text.slice(cursor, number.index) + format(number.value * progress, number);
        cursor = number.index + number.length;
      });

      node.data = output + text.slice(cursor);
    });
  };
}

function play(element, render) {
  const duration = Number(element.dataset.countDuration) || DEFAULT_DURATION;
  const delay = Number(element.dataset.countDelay) || 0;
  let start = null;

  const frame = now => {
    start ??= now;
    const t = Math.min((now - start) / duration, 1);

    render(easeOutCubic(t));

    if (t < 1) {
      requestAnimationFrame(frame);
    }
  };

  setTimeout(() => requestAnimationFrame(frame), delay);
}

export function initCountUp(selector = '[data-count-up]', { root = document } = {}) {
  if (!root || typeof root.querySelectorAll !== 'function') {
    return;
  }

  const elements = [...root.querySelectorAll(selector)].filter(
    element => element.dataset.pfyCountObserved !== 'true'
  );

  if (elements.length === 0 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const renders = new Map();
  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          play(entry.target, renders.get(entry.target));
        }
      });
    },
    // Start a little after the element appears, so the count is seen, not missed at the screen edge.
    { rootMargin: '0px 0px -10% 0px' }
  );

  elements.forEach(element => {
    const render = prepare(element);

    element.dataset.pfyCountObserved = 'true';
    renders.set(element, render);
    render(0);
    observer.observe(element);
  });
}

export default { initCountUp };
